import { useEffect, useState } from 'react';
import AnimatedBackground, { AnimatedBackgroundU } from '../../Components/AnimatedBackground';
import RocketIcon from '../../assets/svg/utils/rocket/rocket-highlighted.svg';
import ProgressBar from '../../Components/ProgressBar';
import { useAppDispatch, useAppSelector } from '../../store';
import eventEmitter, { emitGameEvent, GameEvents } from '../Home/eventEmiter';
import {
  claimCharacterUpdateRewards,
  getCharacterUpdate,
  getCharacterUpdateReward,
  getMe,
  updateCharacter,
} from '../../store/reducers/profile/asyncActions';
import {
  UpgradeCharacterResponse,
} from '../../types';
import UpgradePlatform from './platform';
import GlobalGame from '../../PhaserGameClass/GlobalGame';
import NotEnoughFundsModal from './components/NotEnoughFundsModal';
import UpgradeButtons from './UpgradeButtons';
import { TPageState } from './types';

function Upgrade() {
  const {
    nextCharacterUpdate, userData, characterUpdateRewards, characterUpdatePending, updateCharacterResponse,
  } = useAppSelector(
    ({ profile }) => profile,
  );
  const dispatch = useAppDispatch();
  const [background, setBackground] = useState<AnimatedBackgroundU>('upgradePage');
  const [pageState, setPageState] = useState<TPageState>('upgrade');

  useEffect(() => {
    const handleSceneLoaded = () => {
      if (userData?.character_progress) {
        emitGameEvent({ event: GameEvents.SET_CHARACTER_PROGRESS, characterProgress: userData.character_progress });
      }
    };
    eventEmitter.on(GameEvents.UPGRADE_SCENE_LOADED, handleSceneLoaded);
    return () => {
      eventEmitter.off(GameEvents.UPGRADE_SCENE_LOADED, handleSceneLoaded);
    };
  }, [userData?.character_progress]);

  useEffect(() => {
    GlobalGame.scene.stop('PreloadScene');
    GlobalGame.scene.stop('AviatorScene');
    GlobalGame.updateParent('upgrade-scene');
    GlobalGame.scene.start('UpgradeScene');
    if (characterUpdateRewards?.length) {
      setPageState('claim-reward');
      setBackground('upgradeUnlockLevel');
    }
  }, []);

  useEffect(() => {
    dispatch(getCharacterUpdateReward());
  }, []);

  const handleUpgrade = async () => {
    const resp = await dispatch(updateCharacter());

    if (resp.error) {
      setPageState('not-enough-funds');

      return;
    }

    if (resp.payload.full_upgrade) {
      dispatch(getCharacterUpdateReward());
    }
    emitGameEvent({ event: GameEvents.SET_NEW_CHARACTER_ITEM, upgradeData: resp.payload, setNewBaseSkin: false });

    await dispatch(getMe());
    await dispatch(getCharacterUpdate());
  };
  const handleBackgroundChange = () => {
    if (background === 'upgradePage') {
      setPageState('landing');
      setBackground('upgradeUnlockLevel');
    } else {
      setBackground('upgradePage');
    }
  };
  useEffect(() => {
    eventEmitter.on(GameEvents.ON_FLY_TO_THE_MOON_OVER, handleBackgroundChange);
    return () => {
      eventEmitter.off(GameEvents.ON_FLY_TO_THE_MOON_OVER, handleBackgroundChange);
    };
  }, []);

  useEffect(() => {
    const handleLanding = () => {
      if (background === 'upgradePage') {
        setPageState('claim-reward');
      } else {
        setBackground('upgradePage');
      }
    };
    eventEmitter.on(GameEvents.ON_LANDING_END, handleLanding);
    return () => {
      eventEmitter.off(GameEvents.ON_LANDING_END, handleLanding);
    };
  }, []);

  const handleClaimRewards = async () => {
    const ids = characterUpdateRewards?.map((el) => el.id);
    if (ids?.length) {
      const resp = await dispatch(claimCharacterUpdateRewards({ ids }));
      if (!resp.error) {
        if (nextCharacterUpdate) {
          const updateResp = await dispatch(updateCharacter());
          if (!updateResp.error) {
            await dispatch(getMe());
            await dispatch(getCharacterUpdate());
            setPageState('unlocked');
            emitGameEvent({ event: GameEvents.SET_NEW_CHARACTER_ITEM, upgradeData: (updateResp.payload as UpgradeCharacterResponse), setNewBaseSkin: true });
          }
        } else {
          setPageState('upgrade');
          setBackground('upgradePage');
        }
      }
    }
  };
  const handleBackToUpgrade = async () => {
    await dispatch(getMe());
    await dispatch(getCharacterUpdate());
    setPageState('upgrade');
    setBackground('upgradePage');
  };

  const isNextUpgradeLoaded = nextCharacterUpdate !== undefined && nextCharacterUpdate !== null;

  return (
    <>
      <UpgradeButtons
        fullUpgrade={!!updateCharacterResponse?.full_upgrade}
        pageState={pageState}
        isNextUpgradeLoaded={isNextUpgradeLoaded}
        characterUpdatePending={characterUpdatePending}
        upgradePrice={nextCharacterUpdate?.price || 0}
        handleUpgrade={handleUpgrade}
        handleClaimRewards={handleClaimRewards}
        handleBackToUpgrade={handleBackToUpgrade}
        characterUpdateRewards={characterUpdateRewards}
      />
      <div className="">
        <AnimatedBackground backgroundPage={background} />
        {background === 'upgradePage'
              && (
              <div className="absolute w-full bottom-[10vh]">
                <UpgradePlatform />
              </div>
              )}
        <div id="upgrade-scene" className="absolute top-0 left-0 w-full h-full z-[10]" />
        {pageState === 'claim-reward'
              && (
              <div
                className="absolute top-[17vh] left-1/2 transform -translate-x-1/2 leading-[27.66px] font-Bangers text-[26px] z-[20]"
              >
                <div>
                  Congratulations!
                </div>
                <div>
                  {'You\'ve leveled up'}
                </div>
              </div>
              )}
        {pageState === 'unlocked'
              && (
              <div
                className="absolute top-[17vh] left-1/2 transform -translate-x-1/2 leading-[27.66px] font-Bangers text-[26px] z-[20]"
              >
                <div>
                  Unlocked
                </div>
                <div>
                  {nextCharacterUpdate?.skin_name}
                  {' '}
                  outfit!
                </div>
              </div>
              )}
        <div className="absolute bottom-[40.4vh] right-[12.98vw] text-start z-[20]">
          <div className="text-xs font-[700] font-Barlow">
            {userData?.character_progress?.base_skin}
          </div>
          <div className="w-[80%]">
            <ProgressBar
              length={6}
              currentValue={userData?.character_progress?.progress?.owned || 0}
              goal={userData?.character_progress?.progress?.total || 6}
              type="upgrade"
              hideBG
              sectionalType
            />
          </div>
          <div className={`flex gap-1 text-xs font-Barlow ${!nextCharacterUpdate ? 'invisible' : ''}`}>
            <div className=" font-[700]">
              Upgrade
              {' '}
              {nextCharacterUpdate?.slot}
            </div>
            <div className="flex ml-2">
              <div className="mr-1">
                +
                {nextCharacterUpdate?.rewards?.rocket_coins_recovery_per_hour || 0}
                /hr
              </div>
              <img src={RocketIcon} alt="rocket-icon" className="w-[18px]" />
            </div>
          </div>
        </div>
        <NotEnoughFundsModal open={pageState === 'not-enough-funds'} onClose={() => setPageState('upgrade')} />
      </div>
    </>
  );
}

export default Upgrade;
